// @steered AudibleSecurityContext 1.2 2026-09-15
import React, { useState } from 'react';
import G2S2Chat from './G2S2Chat';
import './G2S2ChatContainer.css';

interface DebugFeatures {
    browserPreview: boolean;
    showSessionIds: boolean;
}

interface UserSettings {
    darkMode: boolean;
    notificationSounds: boolean;
}

interface ChatInstance {
    id: number;
    key: string;
    name: string;
    colorIndex: number;
    sessionId: string;
}

// Color palette for tabs
const TAB_COLORS = [
    '#6366f1', // indigo
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#f59e0b', // amber
    '#10b981', // emerald
    '#3b82f6', // blue
    '#ef4444', // red
    '#14b8a6', // teal
];

// Generate a unique session ID (19 chars total)
// Format: g2s2-{10-char-timestamp}-{4-char-random}
const generateSessionId = () => {
    // Use base36 timestamp (shorter than base10)
    const timestamp = Date.now().toString(36);
    // Generate 4 random base36 characters
    const random = Math.random().toString(36).substring(2, 6);
    return `g2s2-${timestamp}-${random}`;
};

// Export tab state interface for use in App.tsx
export interface G2S2TabState {
    chatInstances: ChatInstance[];
    archivedChats: ChatInstance[];
    activeTabId: number;
    editingId: number | null;
    editingName: string;
    setEditingName: (name: string) => void;
    handleTabSwitch: (tabId: number) => void;
    getTabColor: (colorIndex: number) => string;
    addNewChat: () => void;
    removeChat: (id: number) => void;
    restoreChat: (id: number) => void;
    deleteArchivedChat: (id: number) => void;
    startEditing: (id: number, currentName: string) => void;
    saveEdit: (id: number) => void;
    handleKeyDown: (e: React.KeyboardEvent, id: number) => void;
}

// Custom hook to manage G2S2 tab state - can be used by parent components
export const useG2S2Tabs = (): G2S2TabState => {
    const [chatInstances, setChatInstances] = useState<ChatInstance[]>(() => {
        const saved = localStorage.getItem('g2s2-chat-instances');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.error('Failed to parse saved chat instances', e);
            }
        }
        return [{
            id: 1,
            key: `chat-${Date.now()}-1`,
            name: 'Change 1',
            colorIndex: 0,
            sessionId: generateSessionId()
        }];
    });

    const [archivedChats, setArchivedChats] = useState<ChatInstance[]>(() => {
        const saved = localStorage.getItem('g2s2-archived-chats');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.error('Failed to parse archived chats', e);
            }
        }
        return [];
    });
    
    const [activeTabId, setActiveTabId] = useState(() => {
        const saved = localStorage.getItem('g2s2-active-tab');
        return saved ? parseInt(saved) : 1;
    });
    
    const [nextId, setNextId] = useState(() => {
        const saved = localStorage.getItem('g2s2-next-id');
        return saved ? parseInt(saved) : 2;
    });
    
    const [nextColorIndex, setNextColorIndex] = useState(() => {
        const saved = localStorage.getItem('g2s2-next-color');
        return saved ? parseInt(saved) : 1;
    });
    
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState('');

    // Save state to localStorage whenever it changes
    React.useEffect(() => {
        localStorage.setItem('g2s2-chat-instances', JSON.stringify(chatInstances));
    }, [chatInstances]);

    React.useEffect(() => {
        localStorage.setItem('g2s2-archived-chats', JSON.stringify(archivedChats));
    }, [archivedChats]);

    React.useEffect(() => {
        localStorage.setItem('g2s2-active-tab', activeTabId.toString());
    }, [activeTabId]);

    React.useEffect(() => {
        localStorage.setItem('g2s2-next-id', nextId.toString());
    }, [nextId]);

    React.useEffect(() => {
        localStorage.setItem('g2s2-next-color', nextColorIndex.toString());
    }, [nextColorIndex]);

    const handleTabSwitch = (tabId: number) => {
        const chat = chatInstances.find(c => c.id === tabId);
        if (chat) {
            console.log(`Switched to G2S2 session: ${chat.sessionId} for "${chat.name}"`);
            setActiveTabId(tabId);
        }
    };

    const getTabColor = (colorIndex: number) => {
        return TAB_COLORS[colorIndex % TAB_COLORS.length];
    };

    const addNewChat = () => {
        const newChat: ChatInstance = {
            id: nextId,
            key: `chat-${Date.now()}-${nextId}`,
            name: `Change ${nextId}`,
            colorIndex: nextColorIndex,
            sessionId: generateSessionId()
        };
        setChatInstances([...chatInstances, newChat]);
        setActiveTabId(nextId);
        setNextId(nextId + 1);
        setNextColorIndex((nextColorIndex + 1) % TAB_COLORS.length);
        console.log(`New G2S2 session created: ${newChat.sessionId} for "${newChat.name}"`);
    };

    const removeChat = (id: number) => {
        if (chatInstances.length > 1) {
            const chatToRemove = chatInstances.find(c => c.id === id);
            if (chatToRemove) {
                setArchivedChats([chatToRemove, ...archivedChats]);
                console.log(`G2S2 session archived: ${chatToRemove.sessionId} for "${chatToRemove.name}"`);
            }

            const newInstances = chatInstances.filter(chat => chat.id !== id);
            setChatInstances(newInstances);
            if (activeTabId === id && newInstances.length > 0) {
                setActiveTabId(newInstances[0].id);
            }
        }
    };

    const restoreChat = (id: number) => {
        const chatToRestore = archivedChats.find(c => c.id === id);
        if (chatToRestore) {
            setChatInstances([...chatInstances, chatToRestore]);
            setArchivedChats(archivedChats.filter(c => c.id !== id));
            setActiveTabId(chatToRestore.id);
            console.log(`G2S2 session restored: ${chatToRestore.sessionId} for "${chatToRestore.name}"`);
        }
    };

    const deleteArchivedChat = (id: number) => {
        const chatToDelete = archivedChats.find(c => c.id === id);
        if (chatToDelete) {
            setArchivedChats(archivedChats.filter(c => c.id !== id));
            // Clean up localStorage for this session
            const storageKey = `g2s2-chat-${chatToDelete.sessionId}`;
            localStorage.removeItem(`${storageKey}-messages`);
            localStorage.removeItem(`${storageKey}-kb-session`);
            localStorage.removeItem(`${storageKey}-show-form`);
            localStorage.removeItem(`${storageKey}-form-data`);
            localStorage.removeItem(`${storageKey}-slack-ready`);
            localStorage.removeItem(`${storageKey}-validation-url`);
            localStorage.removeItem(`${storageKey}-show-validation`);
            localStorage.removeItem(`${storageKey}-timer-end`);
            localStorage.removeItem(`${storageKey}-weblab-id`);
            localStorage.removeItem(`${storageKey}-slack-weblab`);
            localStorage.removeItem(`${storageKey}-slack-description`);
            localStorage.removeItem(`${storageKey}-preview-surface`);
            localStorage.removeItem(`${storageKey}-session-config`);
            console.log(`G2S2 session permanently deleted: ${chatToDelete.sessionId} for "${chatToDelete.name}"`);
        }
    };

    const startEditing = (id: number, currentName: string) => {
        setEditingId(id);
        setEditingName(currentName);
    };

    const saveEdit = (id: number) => {
        if (editingName.trim()) {
            setChatInstances(chatInstances.map(chat =>
                chat.id === id ? { ...chat, name: editingName.trim() } : chat
            ));
        }
        setEditingId(null);
        setEditingName('');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingName('');
    };

    const handleKeyDown = (e: React.KeyboardEvent, id: number) => {
        if (e.key === 'Enter') {
            saveEdit(id);
        } else if (e.key === 'Escape') {
            cancelEdit();
        }
    };

    return {
        chatInstances,
        archivedChats,
        activeTabId,
        editingId,
        editingName,
        setEditingName,
        handleTabSwitch,
        getTabColor,
        addNewChat,
        removeChat,
        restoreChat,
        deleteArchivedChat,
        startEditing,
        saveEdit,
        handleKeyDown
    };
};

interface G2S2ChatContainerProps {
    debugFeatures?: DebugFeatures;
    userSettings?: UserSettings;
    tabState: G2S2TabState;
}

const G2S2ChatContainer = ({ debugFeatures, userSettings, tabState }: G2S2ChatContainerProps) => {
    const { chatInstances, activeTabId, getTabColor } = tabState;

    // Log initial session on mount
    React.useEffect(() => {
        const initialChat = chatInstances[0];
        if (initialChat) {
            console.log(`G2S2 session initialized: ${initialChat.sessionId} for "${initialChat.name}"`);
        }
    }, []);

    return (
        <div className="g2s2-chat-container-wrapper">
            <div className="g2s2-chat-instances">
                {chatInstances.map((chat) => {
                    const tabColor = getTabColor(chat.colorIndex);
                    const isActive = activeTabId === chat.id;
                    return (
                        <div
                            key={chat.key}
                            className={`g2s2-chat-instance ${isActive ? 'active' : ''}`}
                        >
                            <G2S2Chat 
                                debugFeatures={debugFeatures}
                                userSettings={userSettings}
                                tabColor={tabColor} 
                                tabName={chat.name}
                                sessionId={chat.sessionId}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default G2S2ChatContainer;
